import React, { useEffect, useState } from 'react';
import { Loader2, WifiOff } from 'lucide-react';
import { ReconnectionState } from '@/contexts/SignalingContext.tsx';

interface NetworkErrorOverlayProps {
  isConnected: boolean;
  reconnectionState: ReconnectionState | null;
  onRetry: () => void;
  sessionActive: boolean;
}

export function NetworkErrorOverlay({
  isConnected,
  reconnectionState,
  onRetry,
  sessionActive
}: NetworkErrorOverlayProps) {
  const [ showOverlay, setShowOverlay ] = useState(false);

  // Show overlay when WebSocket disconnects (both during active session and in lobby)
  useEffect(() => {
    if (!isConnected) {
      setShowOverlay(true);
    } else if (isConnected) {
      setShowOverlay(false);
    }
  }, [ isConnected ]);

  if (!showOverlay || isConnected) return null;

  // Calculate time until next retry
  const getRetryText = () => {
    if (!reconnectionState) return 'Preparing to reconnect...';

    if (!reconnectionState.nextRetryTime) {
      return 'Attempting to reconnect...';
    }

    const now = Date.now();
    const timeLeft = Math.max(0, reconnectionState.nextRetryTime - now);
    const seconds = Math.ceil(timeLeft / 1000);

    if (seconds <= 0) return 'Connecting now...';
    return `Reconnecting in ${seconds} seconds...`;
  };

  // Determine if we've given up trying to reconnect
  const hasGivenUp = reconnectionState &&
    reconnectionState.nextRetryTime === null &&
    reconnectionState.giveUpTime !== null &&
    Date.now() > reconnectionState.giveUpTime;

  const currentAttempt = reconnectionState?.attempt || 0;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center">
      <div className="bg-[#1E293B]/90 border border-slate-700 rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
        <div className="flex flex-col items-center text-center">
          {hasGivenUp ? (
            <WifiOff className="h-12 w-12 text-red-500 mb-4"/>
          ) : (
            <Loader2 className="h-12 w-12 text-[#0E9BB5] animate-spin mb-4"/>
          )}

          <h3 className="text-xl font-semibold text-white mb-2">
            {hasGivenUp ? 'Connection Failed' : 'Connection Lost'}
          </h3>

          <p className="text-slate-300 mb-2">
            {hasGivenUp
              ? 'Something went wrong, we cannot connect you to the server'
              : sessionActive
                ? 'Your session was interrupted due to a network issue'
                : 'Connection to the server was lost'
            }
          </p>

          {!hasGivenUp && (
            <>
              <p className="text-slate-400 text-sm mb-2">
                {getRetryText()}
              </p>
              {currentAttempt > 0 && (
                <p className="text-slate-500 text-xs">
                  Attempt {currentAttempt}
                </p>
              )}
            </>
          )}

          {hasGivenUp && (
            <button
              onClick={onRetry}
              className="mt-4 px-4 py-2 bg-[#0E9BB5] hover:bg-[#0A8399] text-white rounded-lg transition-colors"
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    </div>
  );
}